import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'

import { getHighestRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { fromCents, toCents, toPositive } from '@ankor-io/common/domain/pay/currency'
import { Role } from '@ankor-io/common/types/account'
import type { Category } from '@ankor-io/common/types/transaction/category'
import {
  type AWXCardActivityEventPayload,
  AWXCardActivityEventStatus,
  type FullTransactionPayload,
  PaymentMethod,
  type UpdateManualTransactionBody,
  VatCode,
} from '@ankor-io/common/types/transaction/transaction'
import { currencies, formatCurrency } from '@ankor-io/common/util/currencies'
import { getPaymentMethodText } from '@ankor-io/common/util/enum-to-string/payment.method'
import {
  getPaymentStatusColour,
  getPaymentStatusIcon,
  getPaymentStatusText,
} from '@ankor-io/common/util/enum-to-string/transaction.payment.status'
import { getVatCodeText } from '@ankor-io/common/util/enum-to-string/vat'

import { useTransaction } from '@/hooks/useTransaction'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Chip } from '@/ui-components/Chip'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import { Listbox, ListboxLabel, ListboxOption } from '@/ui-components/Listbox'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'
import { Textarea } from '@/ui-components/Textarea'

const formSchema = z
  .object({
    vatCode: z.enum([VatCode.VAT_INCLUSIVE, VatCode.VAT_EXCLUSIVE, VatCode.VAT_NA]),
    // Accept empty string as undefined and coerce non-empty to number
    vat: z.preprocess(
      (val) => (val === '' || val === null || typeof val === 'undefined' ? undefined : Number(val)),
      z
        .number()
        .min(1, 'Tax amount must be greater than 0')
        .max(100, 'Tax amount cannot be greater than 100')
        .optional(),
    ),
    categoryId: z.string().optional(),
    vendor: z.string().optional(),
    comments: z.string().optional(),
    paymentMethod: z
      .enum([
        PaymentMethod.ANKOR_PAY,
        PaymentMethod.BOAT_CASH,
        PaymentMethod.BOAT_STOCK,
        PaymentMethod.BOAT_APA_CARD,
        PaymentMethod.PERSONAL_CASH_OR_CARD,
        PaymentMethod.TRANSFER,
        PaymentMethod.OTHER,
      ])
      .optional(),
    originalAmount: z
      .string()
      .min(1, 'Amount is required')
      .refine((val) => {
        const num = parseFloat(val)
        return !isNaN(num) && num >= 0 && num <= 9999999.99
      }, 'Amount must be between 0 and 9,999,999.99'),
    originalCurrencyCode: z.string().min(1, 'Currency is required'),
    conversionRate: z.preprocess(
      (val) => (val === '' || val === null || typeof val === 'undefined' ? undefined : Number(val)),
      z
        .number()
        .min(0, 'Conversion rate must be greater than 0')
        .max(9999999.99, 'Conversion rate must be less than 9,999,999.99')
        .optional(),
    ),
    date: z.string().min(1, 'Date is required'),
  })
  .superRefine((data, ctx) => {
    if (data.vatCode === VatCode.VAT_EXCLUSIVE && (data.vat === undefined || data.vat === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Tax amount is required when Tax is Tax Exclusive',
        path: ['vat'],
      })
    }
  })

type FormData = z.infer<typeof formSchema>

type TransactionEditDetailsProps = {
  transaction: FullTransactionPayload
  onOpenAddCategoryModal: () => void
  categories: Category[]
  areCategoriesLoading: boolean
  selectedCharter?: { id: string; name: string } | null
}

const TransactionEditDetails = ({
  transaction,
  onOpenAddCategoryModal,
  categories,
  areCategoriesLoading,
  selectedCharter,
}: TransactionEditDetailsProps) => {
  const { transactionId } = useParams()
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { setTransaction } = useTransaction()
  const { activeOrganizationId, activeConnectedAccountId, organizations } = useUserContext()

  // To avoid the unknown type casting
  const [awxTransactionData, setAwxTransactionData] = useState<AWXCardActivityEventPayload | null>(null)

  const [isSaving, setIsSaving] = useState(false)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      categoryId: transaction?.category?.id,
      originalCurrencyCode: transaction?.originalAmountCurrencyCode,
      date: transaction?.date.split('T')[0],
      conversionRate: transaction?.conversionRateStr ? Number(transaction.conversionRateStr) : undefined,
      paymentMethod: transaction?.paymentMethod as PaymentMethod,
      originalAmount: transaction?.originalAmountCents
        ? fromCents(transaction.originalAmountCents).toString()
        : undefined,
      vat: transaction?.vat ? Number(transaction.vat) : undefined,
      vendor: transaction?.vendor,
      comments: transaction?.comments,
    },
  })

  useEffect(() => {
    if (!transaction) {
      return
    }

    form.reset({
      categoryId: transaction.category?.id || '',
      originalCurrencyCode: transaction.originalAmountCurrencyCode,
      vatCode: transaction.vatCode || VatCode.VAT_EXCLUSIVE,
      date: transaction.date.split('T')[0],
      conversionRate: transaction.conversionRateStr ? Number(transaction.conversionRateStr) : undefined,
      paymentMethod: transaction.paymentMethod,
      originalAmount: transaction.originalAmountCents
        ? toPositive(fromCents(transaction.originalAmountCents)).toString()
        : undefined,
      vat: transaction.vat ? Number(transaction.vat) : undefined,
      vendor: transaction.vendor,
      comments: transaction.comments,
    })
  }, [transaction, form])

  useEffect(() => {
    if (transaction?.awxActivityEvent) {
      setAwxTransactionData({
        ...(transaction.awxActivityEvent.data as AWXCardActivityEventPayload),
        fees: [
          {
            type: 'ATM Fee',
            amountCents: 100,
            currencyCode: 'USD',
          },
          {
            type: 'Merchant Fee',
            amountCents: 200,
            currencyCode: 'EUR',
          },
        ],
      })
    }
  }, [transaction?.awxActivityEvent])

  const onSubmit = async (data: FormData) => {
    // Trigger validation to ensure all fields are validated
    const isValid = await form.trigger()
    if (!isValid) {
      return
    }

    const updateTransaction = async () => {
      setIsSaving(true)
      const payload: UpdateManualTransactionBody = {
        paymentMethod: data.paymentMethod as PaymentMethod,
        vatCode: data.vatCode,
        date: new Date(data.date).toISOString(),
        ...(data.paymentMethod !== PaymentMethod.ANKOR_PAY &&
          data.paymentMethod !== PaymentMethod.TRANSFER && {
            originalAmountCents: toCents(Number(data.originalAmount)),
            originalAmountCurrencyCode: data.originalCurrencyCode,
          }),
        ...(data.vatCode !== VatCode.VAT_NA && data.vatCode !== VatCode.VAT_INCLUSIVE && { vat: data.vat?.toString() }),
        ...(data.categoryId && { categoryId: data.categoryId }),
        ...(data.vendor && { vendor: data.vendor }),
        ...(data.comments && { comments: data.comments }),
        ...(data.originalCurrencyCode !== transaction.charterCurrencyCode &&
          data.paymentMethod !== PaymentMethod.ANKOR_PAY &&
          data.paymentMethod !== PaymentMethod.TRANSFER && {
            conversionRateStr: data.conversionRate?.toString(),
          }),
        // Charter selection
        charterSelected: selectedCharter ? true : false,
        charterId: selectedCharter?.id || null,
      }

      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to update transaction')
        }

        const updatedTransaction: FullTransactionPayload = await res.json()
        setTransaction(updatedTransaction)
        // Coming from a charter page with a charter selected but then removing it
        // Navigate -1 will no longer be valid, so only allow that if we have a selectedCharter
        if (selectedCharter) {
          navigate(-1)
        } else {
          navigate(`/transactions/${transactionId}`)
        }
      } catch (error) {
        console.error(error)
        throw new Error('Failed to update transaction')
      } finally {
        setIsSaving(false)
      }
    }

    toast.promise(updateTransaction, {
      loading: 'Updating expense...',
      success: 'Expense updated successfully',
      error: 'Failed to update expense',
    })
  }

  return (
    <form
      role="form"
      className="space-y-6"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <div className="space-y-2">
        <Heading level={4}>Expense Details</Heading>
        <div className="flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-3 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
          {/* Date */}
          <Field className="space-y-1">
            <Label>Date</Label>
            <Input
              type="date"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={transaction.paymentMethod === PaymentMethod.ANKOR_PAY}
              {...form.register('date')}
            />
            {form.formState.errors.date && <ErrorMessage>{form.formState.errors.date.message}</ErrorMessage>}
          </Field>

          {/* Vendor/Supplier */}
          <Field className="space-y-1">
            <Label>Vendor/Supplier</Label>
            <Input
              type="text"
              placeholder="Enter vendor/supplier name"
              {...form.register('vendor')}
            />
            {form.formState.errors.vendor && <ErrorMessage>{form.formState.errors.vendor.message}</ErrorMessage>}
          </Field>

          {/* Occupy 1 column for larger screens */}
          <div className="hidden md:block"></div>

          {/* Category */}
          <Field className="space-y-1">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-x-2">
              <Label>Category</Label>
              {activeOrganizationId &&
                isPrivilegedRole(getHighestRole(organizations[activeOrganizationId].roles as [Role, ...Role[]])) && (
                  <Button
                    plain
                    className="w-full sm:w-auto"
                    onClick={onOpenAddCategoryModal}
                  >
                    <Plus data-slot="icon" />
                    New Category
                  </Button>
                )}
            </div>
            {areCategoriesLoading ? (
              <Loading />
            ) : (
              categories.length > 0 && (
                <Listbox
                  className={
                    !isPrivilegedRole(getHighestRole(organizations[activeOrganizationId!].roles as [Role, ...Role[]]))
                      ? 'mt-4'
                      : ''
                  }
                  multiple={false}
                  value={form.watch('categoryId')}
                  onChange={(value: string | undefined) => form.setValue('categoryId', value, { shouldValidate: true })}
                >
                  <ListboxOption value="">Select a category</ListboxOption>
                  {categories.map((category) => (
                    <ListboxOption
                      key={category.id}
                      value={category.id}
                    >
                      <ListboxLabel>
                        <Chip
                          name={category.name}
                          colour={category.color}
                          icon={category.icon}
                        />
                      </ListboxLabel>
                    </ListboxOption>
                  ))}
                </Listbox>
              )
            )}
            {form.formState.errors.categoryId && (
              <ErrorMessage>{form.formState.errors.categoryId.message}</ErrorMessage>
            )}
          </Field>

          {/* Payment Method */}
          <Field className="space-y-1 sm:mt-[0.1875rem]">
            <Label>Payment Method</Label>
            <Select
              {...form.register('paymentMethod')}
              disabled={form.watch('paymentMethod') === PaymentMethod.ANKOR_PAY}
            >
              {Object.values(PaymentMethod).map(
                (paymentMethod, index) =>
                  (form.watch('paymentMethod') === PaymentMethod.ANKOR_PAY ||
                    paymentMethod !== PaymentMethod.ANKOR_PAY) && (
                    <option
                      key={`${paymentMethod}-${index}`}
                      value={paymentMethod}
                    >
                      {getPaymentMethodText(paymentMethod)}
                    </option>
                  ),
              )}
            </Select>
            {form.formState.errors.paymentMethod && (
              <ErrorMessage>{form.formState.errors.paymentMethod.message}</ErrorMessage>
            )}
          </Field>

          {/* Comments */}
          <Field className="col-span-2 md:col-span-3">
            <Label>Comments</Label>
            <Textarea
              placeholder="Add any relevant details..."
              {...form.register('comments')}
            />
            {form.formState.errors.comments && <ErrorMessage>{form.formState.errors.comments.message}</ErrorMessage>}
          </Field>

          {/* Amount */}
          <Field className="space-y-1">
            <Label>Amount</Label>
            <div className="flex gap-x-2 mt-3">
              <div className="basis-40 flex flex-col gap-y-1">
                <Select
                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    form.watch('paymentMethod') === PaymentMethod.ANKOR_PAY ||
                    form.watch('paymentMethod') === PaymentMethod.TRANSFER
                  }
                  {...form.register('originalCurrencyCode')}
                >
                  {currencies.map((currency) => (
                    <option
                      key={currency.code}
                      value={currency.code}
                    >
                      {currency.code}
                    </option>
                  ))}
                </Select>
                {form.formState.errors.originalCurrencyCode && (
                  <ErrorMessage>{form.formState.errors.originalCurrencyCode.message}</ErrorMessage>
                )}
              </div>
              <div className="w-full flex flex-col gap-y-1">
                <Input
                  step="0.01"
                  type="number"
                  placeholder="Enter amount"
                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    form.watch('paymentMethod') === PaymentMethod.ANKOR_PAY ||
                    form.watch('paymentMethod') === PaymentMethod.TRANSFER
                  }
                  {...form.register('originalAmount')}
                />
                {form.formState.errors.originalAmount && (
                  <ErrorMessage>{form.formState.errors.originalAmount.message}</ErrorMessage>
                )}
              </div>
            </div>
          </Field>

          {/* Conversion Rate */}
          <Field className="space-y-1">
            <Label>Conversion Rate</Label>
            {/* Disabled for this condition: Conversion rate is not allowed when the currency code is the same as the charter currency code */}
            <Input
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              type="number"
              step="0.01"
              placeholder="Enter conversion rate"
              disabled={
                form.watch('originalCurrencyCode') === transaction.charterCurrencyCode ||
                ((form.watch('paymentMethod') === PaymentMethod.ANKOR_PAY ||
                  form.watch('paymentMethod') === PaymentMethod.TRANSFER) &&
                  !isPrivilegedRole(getHighestRole(organizations[activeOrganizationId!].roles as [Role, ...Role[]])))
              }
              {...form.register('conversionRate')}
            />
            {form.formState.errors.conversionRate && (
              <ErrorMessage>{form.formState.errors.conversionRate.message}</ErrorMessage>
            )}
          </Field>

          {/* Amount Converted */}
          <Field className="space-y-1">
            <Label>Amount Converted {transaction.charterCurrencyCode}</Label>
            <Input
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              readOnly
              disabled={true}
              value={
                form.watch('originalAmount') && form.watch('conversionRate')
                  ? formatCurrency(
                      Number(form.watch('originalAmount')) * Number(form.watch('conversionRate')),
                      transaction.charterCurrencyCode || transaction.originalAmountCurrencyCode,
                    )
                  : formatCurrency(
                      Number(form.watch('originalAmount')),
                      transaction.charterCurrencyCode || transaction.originalAmountCurrencyCode,
                    )
              }
            />
          </Field>

          {/* Tax */}
          <Field className="space-y-1">
            <Label>Tax</Label>
            <Select {...form.register('vatCode')}>
              {Object.values(VatCode).map((vatCode, index) => (
                <option
                  key={`${vatCode}-${index}`}
                  value={vatCode}
                >
                  {getVatCodeText(vatCode)}
                </option>
              ))}
            </Select>
            {form.formState.errors.vatCode && <ErrorMessage>{form.formState.errors.vatCode.message}</ErrorMessage>}
          </Field>

          {/* Tax Amount */}
          <Field className="space-y-1">
            <Label>Tax Amount (%)</Label>
            <Input
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              type="number"
              placeholder="Enter tax amount"
              disabled={form.watch('vatCode') === VatCode.VAT_NA || form.watch('vatCode') === VatCode.VAT_INCLUSIVE}
              {...form.register('vat')}
            />
            {form.formState.errors.vat && <ErrorMessage>{form.formState.errors.vat.message}</ErrorMessage>}
          </Field>
        </div>
      </div>

      {/* Non-editable transaction details */}
      {(transaction.paymentMethod === PaymentMethod.ANKOR_PAY ||
        transaction.paymentMethod === PaymentMethod.TRANSFER) && (
        <div className="space-y-2">
          <Heading level={4}>Transaction Details</Heading>
          <div className="flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-3 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
            {/* TODO: Show payment status for transfers? */}
            {transaction.paymentMethod === PaymentMethod.ANKOR_PAY && (
              <Field className="space-y-1">
                <Label>Payment Status</Label>
                {transaction.awxActivityEvent && (
                  <div className="mt-4">
                    <Chip
                      colour={getPaymentStatusColour(transaction.awxActivityEvent.status as AWXCardActivityEventStatus)}
                      name={getPaymentStatusText(transaction.awxActivityEvent.status as AWXCardActivityEventStatus)}
                      icon={getPaymentStatusIcon(transaction.awxActivityEvent.type)}
                    />
                  </div>
                )}
              </Field>
            )}

            {awxTransactionData && (
              <>
                {/* Transaction Amount */}
                <Field className="space-y-1">
                  <Label>Transaction Amount</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={formatCurrency(
                      fromCents(awxTransactionData.amountCents) || 0,
                      awxTransactionData.amountCurrencyCode,
                    )}
                  />
                </Field>

                {/* Billed Amount */}
                <Field className="space-y-1">
                  <Label>Billed Amount</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={formatCurrency(
                      fromCents(awxTransactionData.billingAmountCents) || 0,
                      awxTransactionData.billingCurrencyCode,
                    )}
                  />
                </Field>

                {/* Card Number */}
                <Field className="space-y-1">
                  <Label>Card</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={awxTransactionData.card.maskedCardNumber}
                  />
                </Field>

                {/* Card Nickname */}
                <Field className="space-y-1">
                  <Label>Card Nickname</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={awxTransactionData.card.nickName}
                  />
                </Field>

                {/* Occupy 1 column for larger screens */}
                <div className="hidden md:block"></div>

                {/* Merchant */}
                <Field className="space-y-1">
                  <Label>Merchant</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={awxTransactionData.merchant?.name}
                  />
                </Field>

                {/* Merchant Country */}
                <Field className="space-y-1">
                  <Label>Merchant Country</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={awxTransactionData.merchant?.country}
                  />
                </Field>

                {/* Merchant City */}
                <Field className="space-y-1">
                  <Label>Merchant City</Label>
                  <Input
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    readOnly
                    disabled={true}
                    value={awxTransactionData.merchant?.city}
                  />
                </Field>

                {/* Fees */}
                <Field className="space-y-1">
                  <Label>Fees</Label>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {awxTransactionData.fees.length ? (
                      awxTransactionData.fees.map((fee) => (
                        <div key={fee.type}>
                          {fee.type}: {formatCurrency(fromCents(fee.amountCents) || 0, fee.currencyCode)}
                        </div>
                      ))
                    ) : (
                      <div>-</div>
                    )}
                  </div>
                </Field>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Actions */}
      <div className="flex justify-end gap-2">
        <Button
          className="disabled:opacity-50 disabled:cursor-not-allowed"
          outline
          type="button"
          disabled={isSaving}
          onClick={() => navigate(-1)}
        >
          Cancel
        </Button>
        <Button
          className="disabled:opacity-50 disabled:cursor-not-allowed"
          color="primary"
          type="submit"
          disabled={isSaving}
          onClick={() => onSubmit(form.getValues())}
        >
          Save
        </Button>
      </div>
    </form>
  )
}

export default TransactionEditDetails
